import * as React from 'react';
import {
  CopilotProvider,
  LatencyCancel,
  LatencyLoader,
  LatencyWrapper,
  ReferenceGroup,
  ReferenceList,
  Suggestion,
  SuggestionList,
} from '@fluentai/react-copilot';
import { Textarea } from '@fluentai/textarea';
import { CopilotChat, UserMessage, CopilotMessage } from '@fluentai/react-copilot-chat';
import {
  Body1,
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Subtitle2,
  Image,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  useId,
  mergeClasses,
  Body1Strong,
  Text,
} from '@fluentui/react-components';
import { useChatBladeStyles } from './CareerCopilotChat.styles';
import {
  CopilotResponse,
  ChatFeedbackRequest,
  ResponseFeedback,
  ChatMessageAuthor,
  ChatMessage,
  ResponseCategories,
  AutoSuggestDocument,
  BasicProfileData,
  RoleResponse,
} from '@careerhub/api';
import {
  CareerHubModuleReactContext,
  LoadingStates,
  toggleChat,
  sendMessage,
  startOver,
  copilotStateSelector,
  stopGenerating,
  careerStateSelector,
  setUserInfo,
  userProfileStateSelector,
} from '@careerhub/common';
import { parseResponse } from './ParseResponse';
import { Dismiss24Regular, MoreHorizontal24Filled } from '@fluentui/react-icons';
import { useHistory } from 'react-router-dom';
import { extractBaseUrl, extractUrlFromString, getPromptExamples } from '../../utilities/Utilities';
import { LinkReference } from './LinkReference';
import { useSharedStyles } from '@careerhub/common';
import { useDispatch, useSelector } from 'react-redux';
import { CopilotMessageFooter } from './CopilotMessageFooter';
import { Book } from '../images/Book';
import { v4 } from 'uuid';
import { Announced } from 'office-ui-fabric-react';
import { PromptExample } from './CareerCopilotChat.types';

const COPILOT_PROMPT_MAX_LENGTH = 500;

/** Renders the Copilot chat experience: messages, follow-up suggestions, text area, and guide button */
export const CareerCopilotChat = () => {
  const history = useHistory();
  const styles = useChatBladeStyles();
  const sharedStyles = useSharedStyles();
  const { copilotService, telemetryClient, roleLibraryService } = React.useContext(CareerHubModuleReactContext);
  const dispatch = useDispatch();
  const { messages, loadingState, followupQuestions, sessionId, conversationId } = useSelector(copilotStateSelector);
  const { isChatInIntialState } = useSelector(careerStateSelector);
  const { fetchProfileStatus, hrProfile } = useSelector(userProfileStateSelector);
  const [text, setText] = React.useState<string | undefined>('');
  const [cursorLocation, setCursorLocation] = React.useState<number>(-1);
  const [secondCursorLocation, setSecondCursorLocation] = React.useState<number>(-1);
  const [openPopover, setOpenPopover] = React.useState<boolean>(true);
  const [userData, setUserData] = React.useState<BasicProfileData>();
  const [announceText, setAnnounceText] = React.useState('');
  const [announcementId, setAnnouncementId] = React.useState(0);
  const [announceSearchResultText, setAnnounceSearchResultText] = React.useState('');
  const [characterCountAnnouncement, setCharacterCountAnnouncement] = React.useState('');
  const [autoSuggestion, setAutoSuggestion] = React.useState<AutoSuggestDocument[]>([]);
  const [openAutoSuggestion, setOpenAutoSuggestion] = React.useState<boolean>(false);
  const [roleName, setRoleName] = React.useState<string>('');
  const [slashIndex, setSlashIndex] = React.useState<number>(-1);
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  const textAreaWrapperRef = React.useRef<HTMLDivElement>(null);
  const popOverCloseRef = React.useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const copilotGuideRef = React.useRef<HTMLButtonElement>(null);

  const startOverButtonRef = React.useRef<HTMLDivElement>(null);
  const lastMessageRef = React.useRef<HTMLDivElement>(null);
  const firstMessageRef = React.useRef<HTMLDivElement>(null);
  const id = useId();
  const characterLimitDescriptionId = useId('copilot-character-limit');
  const promptExamples = getPromptExamples();

  React.useEffect(() => {
    if (!characterCountAnnouncement) return;

    const announcementTimer = window.setTimeout(() => setCharacterCountAnnouncement(''), 1000);
    return () => window.clearTimeout(announcementTimer);
  }, [characterCountAnnouncement]);

  // Handle scenario when user clicks on a prompt example and the cursor location is set to the entity location
  React.useEffect(() => {
    if (cursorLocation > 0) {
      var copilotElement = textAreaRef.current as HTMLTextAreaElement;
      copilotElement.setSelectionRange(cursorLocation, cursorLocation);
      setCursorLocation(-1);
    }
  }, [text, cursorLocation]);
  React.useEffect(() => {
    if (secondCursorLocation > 0) {
      var copilotElement = textAreaRef.current as HTMLTextAreaElement;
      copilotElement.focus();
      copilotElement.setSelectionRange(secondCursorLocation, secondCursorLocation);
    }
  }, [secondCursorLocation]);

  /** Chat with Copilot Service */
  const handleSubmit = React.useCallback(
    (prompt: string) => {
      if (prompt) {
        setAnnounceText(`${prompt} text sent`);
      }
      setText('');
      if (!prompt) return;
      setOpenPopover(false);
      dispatch(
        sendMessage({
          messageId: v4(),
          author: ChatMessageAuthor.User,
          messageText: prompt,
          createdTimestamp: new Date(Date.now())
            .toISOString()
            .replace(/[^0-9]/g, '')
            .slice(0, -3),
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  // Handle scenario when user clicks on a prompt example
  const handlePromptExampleSubmit = async (prompt: PromptExample) => {
    if (prompt) {
      var copilotElement = textAreaRef.current;
      if (copilotElement) {
        if (prompt.entities.length > 0) {
          var searchText =
            (prompt.entities[0].taxonomyTypeId == 'profession' ? userData?.professionName : roleName) ?? '';
          await getAutoSuggestedRoles(searchText, prompt.entities[0].taxonomyTypeId);
          setCursorLocation(prompt.entities[0].cursorLocation + 1);
          setSlashIndex(prompt.entities[0].cursorLocation);
        }
        copilotElement.focus();
        setAnnounceText(`${prompt.prompt} text updated to text area`);
        setText(prompt.promptToDisplay);
      }
    }
    if (!prompt) return;
  };

  const scrollDiv = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    scrollDiv.current?.scrollTo({ top: scrollDiv.current.scrollHeight });
    if (!isChatInIntialState) {
      startOverButtonRef.current?.focus();
    }
  });

  React.useEffect(() => {
    if (loadingState === LoadingStates.SUCCEEDED) {
      const message: ChatMessage | CopilotResponse = messages[messages.length - 1];
      if ((message as CopilotResponse).reply !== undefined) {
        const reply = (message as CopilotResponse).reply || '';
        setAnnounceText(reply);
        lastMessageRef.current?.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingState]);

  React.useEffect(() => {
    const loadUserDetails = async () => {
      const data = userData;
      if (data) {
        const roleId = data.standardTitleId || '';
        if (data.standardTitleId) {
          roleLibraryService
            .getRoleDetails(roleId)
            .then((roleDetails: RoleResponse) => {
              const currentRoleName = roleDetails.name;
              setRoleName(currentRoleName);

              // Chain getCareerPath after getRoleDetails so we have the role name
              return roleLibraryService
                .getCareerPath(roleId)
                .then((nextRole) =>
                  dispatch(
                    setUserInfo({
                      profession: data.professionName ?? '',
                      discipline: data.disciplineName ?? '',
                      disciplineId: data.disciplineId ?? '',
                      careerStage: data.careerStageName ?? '',
                      role: currentRoleName ?? '',
                      nextRole: nextRole[0]?.name ?? '',
                    })
                  )
                )
                .catch(() =>
                  dispatch(
                    setUserInfo({
                      profession: data?.professionName ?? '',
                      discipline: data?.disciplineName ?? '',
                      disciplineId: data?.disciplineId ?? '',
                      careerStage: data?.careerStageName ?? '',
                      role: currentRoleName ?? '',
                      nextRole: '',
                    })
                  )
                );
            })
            .catch((error) => {
              console.log(error);
              dispatch(
                setUserInfo({
                  profession: data?.professionName ?? '',
                  discipline: data?.disciplineName ?? '',
                  disciplineId: data?.disciplineId ?? '',
                  careerStage: data?.careerStageName ?? '',
                  role: '',
                  nextRole: '',
                })
              );
            });
        }
      }
    };
    loadUserDetails();
  }, [userData, roleLibraryService, dispatch]);

  React.useEffect(() => {
    if (hrProfile) {
      setUserData(hrProfile);
    }
  }, [fetchProfileStatus, hrProfile]);

  const renderUserMessage = (text: string) => {
    return (
      <UserMessage tabIndex={0} accessibleHeading={<div>User said</div>}>
        {text}
      </UserMessage>
    );
  };

  const handleCitationClick = (url: string) => {
    let urls = extractUrlFromString(url);
    let baseUrl = extractBaseUrl(url);
    if (urls?.[0] != null) {
      //If UAT or prod endpoint
      if (
        baseUrl?.toLowerCase().includes('rolelibrary-client-uat') ||
        baseUrl?.toLowerCase().includes('msrolelibrary')
      ) {
        const navigationUrl = new URL(url.toLowerCase().indexOf('/profession') == -1 ? urls[0] : urls[2]);
        history.push(navigationUrl.pathname);
      } else {
        window.open(url);
      }
    }
  };

  // Handle scenario when user clicks on a auto suggested role
  const handleSuggestClick = (value: string) => {
    const startIndex = slashIndex;
    var textArea = text;
    var copilotElement = textAreaRef.current as HTMLTextAreaElement;
    const endIndex = copilotElement.selectionStart ?? startIndex;
    if (startIndex !== -1) {
      textArea =
        text?.slice(0, startIndex) + value + (endIndex !== undefined && endIndex !== -1 ? text?.slice(endIndex) : '');
    }
    setText(textArea);
    setAutoSuggestion([]);
    setOpenAutoSuggestion(false);
    setSlashIndex(-1);
    const secondCursor: number = textArea?.indexOf('/') || -1;
    if (secondCursor > 0) {
      setSecondCursorLocation(secondCursor + 1);
    }
  };

  const handleFeedbackSubmit = (
    commentResponse: string,
    responseFeedback: ResponseFeedback,
    copilotResponse: CopilotResponse,
    responseCategories: ResponseCategories[]
  ): boolean => {
    const messageIndex = messages
      .filter((m): m is CopilotResponse => 'copilotMessageId' in m)
      .findIndex((message) => message == copilotResponse);
    const chatMessages = messages.filter((m): m is ChatMessage => 'messageId' in m);
    const userMessage = chatMessages[messageIndex];
    const feedback: ChatFeedbackRequest = {
      usageUserId: '',
      conversationId: conversationId,
      copilotResponseFeedback: responseFeedback,
      verboseFeedback: commentResponse,
      sessionId: sessionId,
      copilotMessageId: copilotResponse.copilotMessageId ?? 'ID returned Undefined',
      copilotMessageTimestamp: copilotResponse.createdTimestamp ?? 'ID returned Undefined',
      userMessageId: userMessage.messageId,
      userMessageTimestamp: userMessage.createdTimestamp,
      responseCategories: responseCategories,
      flightingInformation: '',
    };
    try {
      copilotService.saveChatFeedback(feedback);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  /** Renders message from Copilot Service */
  // TODO consider caching renders, this is a lot of work to do on every render
  const renderBotMessage = (response: CopilotResponse, index: number) => {
    const parsedResponse = parseResponse(response, handleCitationClick);
    const referencesLength = Object.keys(parsedResponse.referenceURLs).length;
    //TODO: revert back after the link type and logic is clear
    const hasCitations = referencesLength > 0;
    const isPlural = referencesLength !== 1;
    return (
      // TODO use one className for simplicity and to support both cards and links in one message
      <CopilotMessage
        className={hasCitations ? styles.chatMessageBody : styles.botMessageBody}
        tabIndex={0}
        ref={index === messages.length - 1 ? lastMessageRef : null}
      >
        {/* Natural language + inline citation buttons, if they exist */}
        {parsedResponse.replyElements}
        {/* Footer and references (either cards or links) */}
        <>
          <CopilotMessageFooter
            handleDialogSubmit={(
              commentResponse: string,
              responseFeedback: ResponseFeedback,
              responseCategories: ResponseCategories[]
            ) => handleFeedbackSubmit(commentResponse, responseFeedback, response, responseCategories)}
            style={{ marginTop: '8px' }}
          />
          {hasCitations && (
            <ReferenceGroup
              className={styles.referenceGroup}
              label={`${referencesLength} reference${isPlural ? 's' : ''}`}
            >
              <ReferenceList>
                {Object.keys(parsedResponse.referenceURLs).map((url, index) => {
                  return (
                    <LinkReference
                      index={index + 1}
                      url={url}
                      linkText={parsedResponse.referenceURLs[url]}
                      handleCitationClick={handleCitationClick}
                    />
                  );
                })}
              </ReferenceList>
            </ReferenceGroup>
          )}
        </>
      </CopilotMessage>
    );
  };

  /** Renders messages to and from Copilot Service */
  const renderChatMessages = () => {
    const cardContents: JSX.Element[] = [];
    messages.forEach((message, index) => {
      'messageId' in message
        ? cardContents.push(renderUserMessage(message.messageText))
        : cardContents.push(renderBotMessage(message, index));
    });

    return cardContents;
  };

  function HighlightPromptExample(val: { text: string }) {
    const parts = val.text.split(/(<[^>]*>)/g);
    return (
      <>
        {parts.map((word, index) =>
          word.startsWith('<') && word.endsWith('>') ? (
            <span key={index} className={styles.promptExampleHighlight}>
              {word.substring(1, word.length - 1)}
            </span>
          ) : (
            <React.Fragment key={index}>{word}</React.Fragment>
          )
        )}
      </>
    );
  }

  const getAutoSuggestedRoles = async (suggestionText: string, taxonomyTypeId: string | undefined = undefined) => {
    if (suggestionText?.length > 0) {
      try {
        const suggestions = await roleLibraryService.getAutoSuggestedRoles(suggestionText, taxonomyTypeId);
        setAutoSuggestion(suggestions);
        setOpenAutoSuggestion(suggestions.length > 0);
        setAnnounceSearchResultText(
          suggestions.length > 0 ? `showing ${suggestions.length} results` : 'No matches found'
        );
        textAreaRef.current?.focus();
      } catch (error) {
        console.log('an error occured calling getAutoSuggestedRoles: ', error);
      }
    } else {
      setAutoSuggestion([]);
      setOpenAutoSuggestion(false);
    }
  };

  // Usage

  return (
    <>
      <CopilotProvider mode="sidecar" className={styles.provider} role="complementary">
        <div className={sharedStyles.flexSpaceBetween} style={{ padding: '16px', alignItems: 'center' }}>
          <div className={styles.headerLeft}>
            <Image src="/assets/botSparkleWithColor.svg" alt="Career assistant logo" />
            <Subtitle2 style={{ margin: '0px 0px 2px 0px' }} as="span">
              Role library assistant
            </Subtitle2>
          </div>
          {/* When refactoring, please ensure this button's touch target doesn't become too tall! */}
          <Menu>
            <MenuTrigger>
              <Button
                icon={<MoreHorizontal24Filled />}
                className={styles.startOverButton}
                appearance="transparent"
                aria-label="More"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  onClick={() => {
                    telemetryClient.logButtonClick('Copilot', 'CopilotStartOver', {
                      conversationId: conversationId,
                    });
                    dispatch(startOver());
                    setAnnounceText('Chat cleared');
                    setAnnouncementId((currentId) => currentId + 1);
                  }}
                  ref={startOverButtonRef}
                  aria-label="Copilot Start Over"
                >
                  Start Over
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          <Button
            icon={<Dismiss24Regular />}
            className={styles.buttonColor}
            onClick={() => {
              telemetryClient.logButtonClick('Copilot', 'CopilotDismiss', {
                conversationId: conversationId,
              });
              dispatch(toggleChat());
            }}
            appearance="transparent"
            aria-label="Close Copilot"
          />
        </div>
        <CopilotChat ref={scrollDiv} className={styles.chat} role="log">
          {userData && (
            <CopilotMessage
              tabIndex={0}
              className={mergeClasses(styles.card, styles.initialMessage)}
              accessibleHeading={<div>Copilot said</div>}
            >
              <Body1>{`Hi ${userData?.preferredFullName || ''},`}</Body1>
              <Body1>Ready to explore?</Body1>
              <Body1>
                Answers generated will be based solely on the content available in the Role Library, which describes
                roles at Microsoft based on profession, discipline, and career stage.
              </Body1>
            </CopilotMessage>
          )}
          {renderChatMessages()}
          {announceText && <Announced key={announcementId} message={announceText} />}
          {loadingState === LoadingStates.STARTED && (
            <LatencyWrapper className={styles.latencyWrapper}>
              <LatencyLoader header={'Gathering information...'} className={styles.latency} />
              <LatencyCancel onClick={() => dispatch(stopGenerating())}>Stop generating</LatencyCancel>
            </LatencyWrapper>
          )}
        </CopilotChat>
        <div className={styles.inputArea}>
          {followupQuestions.length > 0 && (
            <SuggestionList>
              {followupQuestions.map((question) => {
                return (
                  <Suggestion
                    onClick={() => {
                      handleSubmit(question);
                      telemetryClient.logButtonClick('Copilot', 'FollowUpQuestions', {
                        conversationId: conversationId,
                      });
                    }}
                    disabled={loadingState === LoadingStates.STARTED}
                  >
                    {question}
                  </Suggestion>
                );
              })}
            </SuggestionList>
          )}
          <div className={styles.bookPopOverContainer}>
            <Popover
              positioning={'above-start'}
              open={openPopover}
              inline
              onOpenChange={(event, data) => {
                setAnnounceText(
                  data.open ? 'Copilot guide menu button Expanded' : 'Copilot guide menu button Collapsed'
                );
                setOpenPopover(data.open);
                if (!data.open && 'key' in event && event.key === 'Escape') {
                  copilotGuideRef.current?.focus();
                }
              }}
            >
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  aria-label="Copilot guide"
                  style={{ marginLeft: 'auto' }}
                  appearance="transparent"
                  icon={<Book />}
                  ref={copilotGuideRef}
                />
              </PopoverTrigger>
              <PopoverSurface aria-labelledby={id} className={styles.popoverSurface}>
                <div className={styles.suggestionHeader}>
                  <Body1Strong as="h2" id={id} className={styles.popoverTitle}>
                    Prompt examples
                  </Body1Strong>
                  <Button
                    appearance="transparent"
                    icon={<Dismiss24Regular />}
                    className={styles.buttonColor}
                    onClick={() => {
                      setAnnounceText('');
                      setOpenPopover(false);
                    }}
                    aria-label="close"
                    tabIndex={0}
                    ref={popOverCloseRef}
                  />
                </div>
                <ul className={styles.suggestionsPopover} aria-label="Prompt examples">
                  {promptExamples.map((s) => {
                    return (
                      <li key={s.id}>
                        <Button
                          appearance="transparent"
                          onClick={() => {
                            handlePromptExampleSubmit(s);
                            telemetryClient.logButtonClick('Copilot', 'PopoverSuggestions', {
                              conversationId: conversationId,
                            });
                          }}
                          className={styles.promptExample}
                        >
                          <HighlightPromptExample text={s.prompt} />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </PopoverSurface>
            </Popover>
          </div>
          <div ref={textAreaWrapperRef}>
            <span id={characterLimitDescriptionId} className={styles.srOnly}>
              Maximum {COPILOT_PROMPT_MAX_LENGTH} characters
            </span>
            <Textarea
              role="textbox"
              showCount
              maxLength={COPILOT_PROMPT_MAX_LENGTH}
              aria-describedby={characterLimitDescriptionId}
              onFocus={() => {
                if (openPopover) {
                  setOpenPopover(!openPopover);
                }
              }}
              ref={textAreaRef}
              onChange={async (_e, d) => {
                const str = d.value;
                var cursorPos = _e.target.selectionStart;
                setText(str);
                setCharacterCountAnnouncement(`${str.length} out of ${COPILOT_PROMPT_MAX_LENGTH} characters entered`);
                // If there is a slash between before current cursor location, take the index of the last slash
                var slashPos = str.substring(0, cursorPos).lastIndexOf('/');

                // add the <= 100 char limit to avoid hitting the character limit of the auto suggest API
                if (slashPos !== -1 && cursorPos - slashPos <= 100) {
                  const suggestionText = str.slice(slashPos + 1, cursorPos);
                  var searchText = (suggestionText.length > 0 ? suggestionText : roleName) ?? '';
                  setSlashIndex(slashPos);
                  await getAutoSuggestedRoles(searchText);
                }
              }}
              onSubmit={() => {
                handleSubmit(text ?? '');
                telemetryClient.logButtonClick('Copilot', 'UserTextSubmitQuestion', {
                  conversationId: conversationId,
                });
              }}
              characterCount={text?.length}
              value={text}
              aria-label="Copilot text area"
              placeholder={"Ask a question, or type '/' to reference a role, profession or discipline"}
              disabled={loadingState === LoadingStates.STARTED}
              onKeyDown={(event) => {
                if (event.code == 'ArrowUp') {
                  firstMessageRef.current?.focus();
                } else if (event.code !== 'Space') {
                  setAutoSuggestion([]);
                  setOpenAutoSuggestion(false);
                  setSlashIndex(-1);
                }
              }}
            />
            <div role="status" aria-live="polite" aria-atomic="true" className={styles.srOnly}>
              {characterCountAnnouncement}
            </div>
          </div>
          <Menu open={openAutoSuggestion} positioning={{ target: textAreaWrapperRef.current }}>
            <MenuPopover aria-expanded={autoSuggestion.length > 0}>
              <Announced message={announceSearchResultText} aria-live="assertive" />
              <MenuList role="list">
                {autoSuggestion &&
                  autoSuggestion.length > 0 &&
                  autoSuggestion.map((suggestion, index) => {
                    return (
                      <MenuItem
                        role="listitem"
                        className={mergeClasses(
                          styles.textAreaPopover,
                          index !== 0 ? styles.textAreaPopoverSpacing : ''
                        )}
                        onClick={() => handleSuggestClick(suggestion.name)}
                        ref={index === 0 ? firstMessageRef : null}
                        onKeyDown={(event) => {
                          if (event.code === 'ArrowUp') {
                            firstMessageRef.current?.focus();
                          }
                        }}
                      >
                        <Text size={300} style={{ display: 'block' }}>
                          {suggestion.name}
                        </Text>
                        <Text size={100}>{suggestion.taxonomyTypeId}</Text>
                      </MenuItem>
                    );
                  })}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </CopilotProvider>
    </>
  );
};
